import { LogEntryPhoto } from '../models/logs/photos/LogEntryPhoto.interface';
import * as pdfjsLib from 'pdfjs-dist';

// Set up the worker source for PDF.js
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;

/**
 * Extracts the base filename without extension
 * @param filename The original filename
 * @returns The filename without extension
 */
function getBaseName(filename: string): string {
  // Handle files with multiple dots in the name
  const lastDotIndex = filename.lastIndexOf('.');
  return lastDotIndex !== -1 ? filename.substring(0, lastDotIndex) : filename;
}

/**
 * Converts a PDF file to an array of JPEG images
 * @param file The PDF file to convert
 * @returns Promise resolving to an array of LogEntryPhoto objects
 */
export async function convertPdfToImage(file: File): Promise<LogEntryPhoto[]> {
  try {
    // Create a blob URL for the file
    const blobUrl = URL.createObjectURL(file);
    
    try {
      // Get base filename without extension
      const baseName = getBaseName(file.name);
      
      // Load the PDF document
      const loadingTask = pdfjsLib.getDocument(blobUrl);
      const pdf = await loadingTask.promise;
      
      const photos: LogEntryPhoto[] = [];
      
      // Process each page
      for (let i = 1; i <= pdf.numPages; i++) {
        try {
          const page = await pdf.getPage(i);
          const viewport = page.getViewport({ scale: 1.5 });
          
          const canvas = document.createElement('canvas');
          const context = canvas.getContext('2d');
          if (!context) {
            throw new Error('Failed to get canvas context');
          }
          
          canvas.height = viewport.height;
          canvas.width = viewport.width;
          
          await page.render({
            canvasContext: context,
            viewport: viewport
          }).promise;
          
          // Use JPEG format with quality 0.8 (good balance of quality and size)
          const dataUrl = canvas.toDataURL('image/jpeg', 0.8);
          
          // Create a proper filename: baseFilename-page-N.jpg
          const photoName = pdf.numPages > 1 
            ? `${baseName}-page-${i}.jpg` 
            : `${baseName}.jpg`;
          
          photos.push({
            id: `${Date.now()}-${i}`,
            url: dataUrl,
            name: photoName
          });
        } catch (pageError) {
          console.error(`Error processing page ${i}:`, pageError);
          // Continue with other pages even if one fails
        }
      }
      
      // Clean up the blob URL
      URL.revokeObjectURL(blobUrl);
      
      if (photos.length === 0) {
        throw new Error('No pages were successfully converted');
      }
      
      return photos;
    } catch (error) {
      // Clean up the blob URL in case of error
      URL.revokeObjectURL(blobUrl);
      throw error;
    }
  } catch (error) {
    console.error('Error converting PDF to image:', error);
    throw new Error('Failed to convert PDF to image: ' + (error instanceof Error ? error.message : 'Unknown error'));
  }
}